import React, { useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export const RICH_TEXT_PRESETS = {
  default: {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    formats: ["header", "bold", "italic", "underline", "list", "link"],
  },
  question: {
    toolbar: [
      ["bold", "italic", "underline"],
      ["clean"],
    ],
    formats: ["bold", "italic", "underline"],
  },
  description: {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
    formats: ["bold", "italic", "underline", "list"],
  },
};

export const getRichTextPreset = (mode = "default") => RICH_TEXT_PRESETS[mode] || RICH_TEXT_PRESETS.default;

/** WYSIWYG editor wrapper. Outputs HTML string. */
export function RichText({ value, onChange, testid, placeholder = "", minHeight = 100, mode = "default", ariaLabel }) {
  const preset = getRichTextPreset(mode);
  const modules = useMemo(() => ({ toolbar: preset.toolbar }), [preset]);
  return (
    <div className="rich-text-wrapper" data-testid={testid} style={{ minHeight }} role="group" aria-label={ariaLabel}>
      <ReactQuill theme="snow" value={value || ""} onChange={onChange} modules={modules} formats={preset.formats} placeholder={placeholder} />
    </div>
  );
}

/** Replace non-breaking spaces (U+00A0) and their entity form with a regular space.
    Quill sometimes inserts these when the user pastes formatted content — they
    turn a whole sentence into a single atomic "word" and cause mid-character
    wrapping on narrow viewports. Regular spaces let the browser wrap at word
    boundaries as expected. */
function normalizeSpaces(s) {
  if (!s) return s;
  return s.replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " ");
}

/** Smart renderer: shows sanitized HTML if content looks like HTML, else preserves plain-text line breaks. */
export function HtmlContent({ html, className = "", inline = false }) {
  const raw = normalizeSpaces(html || "");
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw);
  const display = inline ? "inline" : "block";
  if (!looksLikeHtml) {
    return <span data-wrap="responsive" className={`whitespace-pre-line ${display} ${className}`}>{raw}</span>;
  }
  const cleaned = normalizeSpaces(sanitizeHtml(raw));
  return <span data-wrap="responsive" className={`rich-content ${display} ${className}`} dangerouslySetInnerHTML={{ __html: cleaned }} />;
}

const ALLOWED_TAGS = new Set(["p","br","strong","em","u","b","i","ul","ol","li","a","h1","h2","h3","h4","h5","h6","blockquote","span"]);
const ALLOWED_ATTRS = { a: new Set(["href","target","rel"]) };
const DROP_WITH_CONTENT = new Set(["script", "style", "iframe", "object", "embed", "svg", "math", "template"]);
const SAFE_PROTOCOLS = new Set(["http", "https", "mailto", "tel"]);

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function hasSafeUrl(value) {
  const compact = String(value || "").trim().replace(/[\u0000-\u0020\u007f-\u009f]/g, "");
  const scheme = compact.match(/^([a-z][a-z0-9+.-]*):/i);
  return !scheme || SAFE_PROTOCOLS.has(scheme[1].toLowerCase());
}

export function sanitizeHtml(html, Parser = typeof DOMParser === "undefined" ? null : DOMParser) {
  const raw = normalizeSpaces(html || "");
  if (typeof Parser !== "function") return escapeHtml(raw);
  const doc = new Parser().parseFromString(`<div>${raw}</div>`, "text/html");
  const root = doc.body.firstChild;
  walk(root);
  return root ? root.innerHTML : "";
}

function walk(node) {
  if (!node) return;
  const kids = [...node.childNodes];
  for (const k of kids) {
    if (k.nodeType === 1) {
      const tag = k.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        if (DROP_WITH_CONTENT.has(tag)) {
          node.removeChild(k);
          continue;
        }
        const text = k.ownerDocument.createTextNode(k.textContent || "");
        node.replaceChild(text, k);
        continue;
      }
      const allowed = ALLOWED_ATTRS[tag] || new Set();
      [...k.attributes].forEach((attribute) => {
        if (!allowed.has(attribute.name.toLowerCase())) k.removeAttribute(attribute.name);
      });
      if (tag === "a") {
        const href = k.getAttribute("href") || "";
        if (!hasSafeUrl(href)) k.removeAttribute("href");
        const target = k.getAttribute("target");
        if (target && target !== "_blank") k.removeAttribute("target");
        if (target === "_blank") k.setAttribute("rel", "noopener noreferrer");
        else k.removeAttribute("rel");
      }
      walk(k);
    } else if (k.nodeType === 8) {
      node.removeChild(k);
    }
  }
}
